import axios from "@core/plugins/axios.plugin"
import { formatAddress } from "@core/helpers"
import { handleServiceCall } from "@services/helpers"
import type {
    UsageProfile,
    UsageProfileBuildingFormatted,
} from "@modules/usage-profile/types"

// =====================
// Formatage
// =====================

/** Bâtiment brut retourné par l'API */
interface RawBuilding {
    _id: string
    name: string
    adress?: { street?: string; zipcode?: string; city?: string }
    address?: { street?: string; zipcode?: string; city?: string }
    infos?: { niveau?: string }
}

/** Adresse compatible avec formatAddress */
type AddressLike = { street?: string; zipcode?: string; city?: string } | null

/**
 * Formate un profil d'utilisation pour l'affichage
 * @param profile - Profil brut à formater
 * @returns Profil formaté avec buildings
 */
function formatProfile(profile: Record<string, unknown>): UsageProfile {
    const buildings = (profile.buildings as RawBuilding[] | undefined)?.map(
        (building): UsageProfileBuildingFormatted => ({
            _id: building._id,
            name: building.name,
            level: building.infos?.niveau,
            address: formatAddress(
                (building.adress ?? building.address) as AddressLike
            ),
        })
    )
    return {
        ...profile,
        buildings: buildings ?? [],
    } as UsageProfile
}

/**
 * Formate une liste de profils d'utilisation
 * @param profiles - Liste des profils bruts
 * @returns Liste des profils formatés
 */
function formatProfiles(profiles: Record<string, unknown>[]): UsageProfile[] {
    return profiles.map(formatProfile)
}

// =====================
// Service
// =====================

const API_BASE = "/api/metier/company/profil"

/**
 * Service de gestion des profils d'utilisation des bâtiments
 */
export class UsageProfileService {
    /**
     * Récupère la liste des profils d'usage d'une entreprise
     */
    async getUsageProfiles(params: {
        page?: number
        pagesize?: number
        search?: string
        noPage?: boolean
    }): Promise<UsageProfile[]> {
        return handleServiceCall(async () => {
            const { data } = await axios.get<{
                data: Record<string, unknown>[]
            }>(API_BASE, {
                params: {
                    page: params.page,
                    pagesize: params.pagesize ?? 10,
                    search: params.search,
                    noPage: params.noPage ?? false,
                },
            })
            return formatProfiles(data.data ?? [])
        })
    }

    /**
     * Récupère un profil d'usage par son identifiant
     */
    async getUsageProfileById(params: {
        profileId: string
    }): Promise<UsageProfile> {
        return handleServiceCall(async () => {
            const { data } = await axios.get<Record<string, unknown>>(
                `${API_BASE}/${params.profileId}`
            )
            return formatProfile(data)
        })
    }

    /**
     * Récupère les bâtiments liés à un profil d'usage
     */
    async getUsageProfileBuildingsById(params: {
        profileId: string
        format?: boolean
    }): Promise<RawBuilding[]> {
        return handleServiceCall(async () => {
            const { data } = await axios.get<{ result: RawBuilding[] }>(
                `${API_BASE}/${params.profileId}/building`
            )
            const result = data?.result ?? []
            if (params.format) {
                result.forEach((element: RawBuilding) => {
                    ;(element as Record<string, unknown>).address =
                        formatAddress(
                            (element.adress ?? element.address) as AddressLike
                        )
                    ;(element as Record<string, unknown>).level =
                        element.infos?.niveau
                })
            }
            return result
        })
    }

    /**
     * Crée un profil d'usage
     */
    async createUsageProfile(params: {
        profile: Record<string, unknown>
    }): Promise<UsageProfile> {
        return handleServiceCall(async () => {
            const { data } = await axios.post<Record<string, unknown>>(
                API_BASE,
                params.profile
            )
            return formatProfile(data)
        })
    }

    /**
     * Met à jour un profil d'usage
     */
    async updateUsageProfile(params: {
        profileId: string
        profile: Record<string, unknown>
    }): Promise<UsageProfile> {
        return handleServiceCall(async () => {
            const { data } = await axios.put<Record<string, unknown>>(
                `${API_BASE}/${params.profileId}`,
                params.profile
            )
            return formatProfile(data)
        })
    }

    /**
     * Supprime un profil d'usage
     */
    async deleteUsageProfile(params: { profileId: string }): Promise<void> {
        return handleServiceCall(async () => {
            await axios.delete(`${API_BASE}/${params.profileId}`)
        })
    }

    /**
     * Ajoute un profil d'usage à un ou des bâtiments
     */
    async addUsageProfileToBuilding(params: {
        profileId: string
        buildingIds: string[]
    }): Promise<UsageProfile> {
        return handleServiceCall(async () => {
            const { data } = await axios.post<Record<string, unknown>>(
                `${API_BASE}/${params.profileId}/building`,
                { _id_buildings: params.buildingIds }
            )
            return formatProfile(data)
        })
    }

    /**
     * Supprime un ou des bâtiments d'un profil d'usage
     */
    async deleteUsageProfileBuilding(params: {
        profileId: string
        buildingIds: string[]
    }): Promise<void> {
        return handleServiceCall(async () => {
            await axios.delete(`${API_BASE}/${params.profileId}/building`, {
                data: { _id_buildings: params.buildingIds },
            })
        })
    }
}
